import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import Rails from "@rails/ujs";

export default class extends Controller {
  static targets = ["list", "search", "filterPanel", "taskContainer"];

  connect() {
    this.initializeSortable();
    this.initializeTaskClickHandlers();
    this.listenForTurboStreamUpdates();
    this.filterPanelVisible = false;
  }

  disconnect() {
    // Cleanup event handlers
    if (this.sortableLists) {
      this.sortableLists.destroy();
    }
    this.element.querySelectorAll(".task-card").forEach((task) => {
      task.removeEventListener("click", this.handleTaskClick.bind(this));
    });
  }

  // ----------------------------------
  // SortableJS Initializers
  // ----------------------------------
  initializeSortable() {
    // 1) Sorting for the lists
    this.sortableLists = new Sortable(
      this.element.querySelector('[data-sortable-group-value="lists"]'),
      {
        animation: 150,
        draggable: ".kanban-list",
        handle: ".list-handle",
        ghostClass: "sortable-ghost",
        chosenClass: "sortable-chosen",
        dragClass: "sortable-drag",
        onEnd: this.handleListSort.bind(this),
      }
    );

    // 2) Sorting for tasks within each list
    this.element
      .querySelectorAll('[data-sortable-group-value="tasks"]')
      .forEach((el) => {
        new Sortable(el, {
          group: "tasks",
          animation: 150,
          draggable: ".task-card",
          ghostClass: "sortable-ghost",
          chosenClass: "sortable-chosen",
          dragClass: "sortable-drag",
          onEnd: this.handleTaskSort.bind(this),
        });
      });
  }

  initializeTaskClickHandlers() {
    this.element.querySelectorAll(".task-card").forEach((task) => {
      task.addEventListener("click", this.handleTaskClick.bind(this));
    });
  }

  listenForTurboStreamUpdates() {
    document.addEventListener("turbo:before-stream-render", (event) => {
      if (event.target.getAttribute("target") === "lists") {
        this.sortableLists.destroy();
      }
    });

    document.addEventListener("turbo:render", () => {
      this.initializeSortable();
    });
  }

  // ----------------------------------
  // Handle Sorting Lists
  // ----------------------------------
  handleListSort(evt) {
    if (evt.oldIndex === evt.newIndex) return;

    const url = this.element.dataset.sortListsUrl;
    const lists = Array.from(evt.to.children).map((el, index) => ({
      id: el.dataset.listId,
      position: index + 1,
    }));

    // Form-encode the data
    const params = new URLSearchParams({
      lists: JSON.stringify(lists),
    });

    Rails.ajax({
      url,
      type: "PATCH",
      data: params.toString(),
      contentType: "application/x-www-form-urlencoded; charset=UTF-8",
      success: () => this.showSuccess("List order updated"),
      error: () => this.showError("Failed to update list order"),
    });
  }

  // ----------------------------------
  // Handle Sorting Tasks
  // ----------------------------------
  handleTaskSort(evt) {
    if (evt.from === evt.to && evt.oldIndex === evt.newIndex) return;

    const taskId = evt.item.dataset.taskId;
    const boardId = this.element.dataset.boardId;
    const newListId = evt.to.closest("[data-list-id]").dataset.listId;
    const workspaceId = this.element.dataset.workspaceId;

    const url = `/kanban/${workspaceId}/boards/${boardId}/lists/${newListId}/tasks/${taskId}/move`;

    // Form-encode the new list_id and position
    const params = new URLSearchParams({
      "task[list_id]": newListId,
      "task[position]": (evt.newIndex + 1).toString(),
    });

    Rails.ajax({
      url,
      type: "PATCH",
      data: params.toString(),
      contentType: "application/x-www-form-urlencoded; charset=UTF-8",
      success: () => this.showSuccess("Task moved successfully"),
      error: () => {
        this.showError("Failed to move task");
        // Revert UI position on error
        if (evt.from && evt.item) {
          evt.from.insertBefore(evt.item, evt.from.children[evt.oldIndex]);
        }
      },
    });
  }

  // ----------------------------------
  // Handle Task Click
  // ----------------------------------
  handleTaskClick(event) {
    const taskId = event.currentTarget.dataset.taskId;
    this.dispatch("taskClick", { detail: { taskId } });
  }

  // ----------------------------------
  // Flash Helpers
  // ----------------------------------
  showSuccess(message) {
    this.dispatch("notice", { detail: { message, type: "success" } });
  }

  showError(message) {
    this.dispatch("notice", { detail: { message, type: "error" } });
  }
}
